// TaskList.tsx

import "./TaskList.scss";
import WldIcon from "../../../../../assets/icons/wld-icon.svg?react";
import GoldIcon from "../../../../../assets/icons/gold-icon.svg?react";
import UsdcIcon from "../../../../../assets/icons/usdc-icon.svg?react";
import QuarkLogoImg from "../../../../../assets/images/coins/quark-coin.webp";
import ReddLogoImg from "../../../../../assets/images/coins/redd-icon.webp";
import SushiLogoImg from "../../../../../assets/images/coins/sushi-icon.webp";
import OroLogoImg from "../../../../../assets/images/coins/oro-icon.webp";
import OrbLogoImg from "../../../../../assets/images/coins/orb-icon.webp";
import LightBlob from "../../../../../assets/icons/light-blob.svg?react";
import CheckInBadge from "../../../../../assets/icons/check-in-badge.svg?react";
import SurveyBadge from "../../../../../assets/icons/survey-badge.svg?react";
import InlineArrowRight from "../../../../../assets/icons/inline-arrow-right.svg?react";
import { useId, useLayoutEffect, useRef, useState, useEffect } from "react";

// FEED-NEW-CHIPS
import NewChipEnSvg from "../../../../../assets/icons/feed-new-chips/new-chip-en.svg?react";
import NewChipEsSvg from "../../../../../assets/icons/feed-new-chips/new-chip-es.svg?react";
import NewChipPtBrSvg from "../../../../../assets/icons/feed-new-chips/new-chip-ptbr.svg?react";
import NewChipIdSvg from "../../../../../assets/icons/feed-new-chips/new-chip-id.svg?react";
import NewChipThSvg from "../../../../../assets/icons/feed-new-chips/new-chip-th.svg?react";

// FEED-BETA-CHIPS
import BetaChipEnSvg from "../../../../../assets/icons/feed-beta-chips/beta-svg-en.svg?react";
import BetaChipThSvg from "../../../../../assets/icons/feed-beta-chips/beta-svg-th.svg?react";

// Locale → NewChip component mapping
const NEW_CHIP_BY_LOCALE: Record<
    string,
    React.FC<React.SVGProps<SVGSVGElement>>
> = {
    en: NewChipEnSvg,
    es: NewChipEsSvg,
    pt: NewChipPtBrSvg,
    "pt-BR": NewChipPtBrSvg,
    id: NewChipIdSvg,
    th: NewChipThSvg,
};

const BETA_CHIP_BY_LOCALE: Record<
    string,
    React.FC<React.SVGProps<SVGSVGElement>>
> = {
    en: BetaChipEnSvg,
    es: BetaChipEnSvg,
    "pt-br": BetaChipEnSvg,
    id: BetaChipEnSvg,
    th: BetaChipThSvg,
};

function getNewChipForLocale(
    locale: string,
): React.FC<React.SVGProps<SVGSVGElement>> {
    const baseLang = locale.split("-")[0] ?? "en";
    return (
        NEW_CHIP_BY_LOCALE[locale] ??
        NEW_CHIP_BY_LOCALE[baseLang] ??
        NewChipEnSvg
    );
}

function getBetaChipForLocale(
    locale: string,
): React.FC<React.SVGProps<SVGSVGElement>> {
    const baseLang = locale.split("-")[0] ?? "en";
    return (
        BETA_CHIP_BY_LOCALE[locale] ??
        BETA_CHIP_BY_LOCALE[baseLang] ??
        BetaChipEnSvg
    );
}

interface TaskListProps {
    onTaskClick: (taskId: string) => void;
    tasks: z.infer<typeof TaskListItemSchema>[];
}

import { z } from "zod";
import i18n from "../../../../../logic/helpers/i18n";
import { timeAgo } from "../../../../../logic/helpers/timeAgo";
import Skeleton from "../../../../common/Skeleton/Skeleton";
import { useSlice } from "@bridge-applications/provider-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import { useTranslation } from "react-i18next";
import { sendHapticFeedback } from "../../../../../logic/helpers/world/sendHapticFeedback";

export const TaskListItemSchema = z.object({
    id: z.string(),
    type: z.enum([
        "SURVEY",
        "CHECK_IN_CAMPAIGN",
        "WHEEL_SPIN",
        "SCRATCH",
        "BRIDGE_CHECK_IN",
        "REFERRAL",
        "PARTNER_LINK",
        "AB_TEST",
        "GAE",
        "GENERIC_LINK",
        "PARTNER_CAMPAIGN_ACTION",
    ]),
    taskStatus: z.enum(["available", "closed"]),
    userRelation: z.enum(["not-started", "in-progress", "completed"]),
    isActionable: z.boolean(),
    title: z.string(),
    subtitle: z.string().optional(),
    imgUrl: z.string(),
    duration: z.number(), // in ms
    spotsLeft: z.number(),
    createdAt: z.number().optional(),
    // pin meta (optional)
    isPinned: z.boolean().optional(),
    pinLevel: z.enum(["NONE", "SOFT", "HARD"]).optional(),
    pinnedPosition: z.number().int().nonnegative().optional(),
    primaryReward: z.discriminatedUnion("type", [
        z.object({
            type: z.literal("TOKEN"),
            unit: z.enum([
                "USDC",
                "WLD",
                "GOLD",

                // New token units
                "QUARK",
                "REDD",
                "SUSHI",
                "ORO",
                "ORB",
            ]),
            amount: z.number(),
        }),
        z.object({
            type: z.literal("BUNDLE"),
            // TODO
        }),
    ]),
    // Spin-the-wheel extras (optional; for subtitle rendering)
    stwCanSpinNow: z.boolean().optional(),
    // epoch ms when spinning becomes available
    stwCanSpinAt: z.number().optional(),

    // Scratch extras (optional; for subtitle rendering)
    // (legacy scratch removed from feed union; keeping fields for backward compat)
    scratchCanScratchNow: z.boolean().optional(),
    scratchCanScratchAt: z.number().optional(),

    // Bridge check-in extras (optional; for subtitle rendering)
    brCanCheckNow: z.boolean().optional(),
    // epoch ms when check-in becomes available
    brNextEligibleAt: z.number().optional(),
});

// Formats duration in milliseconds to human-readable format
function formatDuration(durationMs: number): string {
    const minutes = Math.floor(durationMs / 60000);
    const hours = Math.floor(durationMs / 3600000);
    const days = Math.floor(durationMs / 86400000);
    const weeks = Math.floor(durationMs / 604800000);

    if (weeks >= 1) {
        return `${weeks} wk`;
    } else if (days >= 1) {
        return `${days} day`;
    } else if (hours >= 1) {
        return `${hours} hr`;
    } else {
        return `${minutes} min`;
    }
}

// Formats a short countdown like "15m" or "2h"
function formatShortCountdown(ms: number): string {
    const nowMs = Math.max(0, ms);
    const totalSecs = Math.ceil(nowMs / 1000);
    const totalMins = Math.ceil(totalSecs / 60);

    if (totalMins < 60) {
        return `${totalMins}m`;
    } else {
        const hours = Math.floor(totalMins / 60);
        const mins = totalMins % 60;
        return `${hours}h${mins > 0 ? ` ${mins}m` : ""}`;
    }
}

// Renders a number with 2 decimals where:
// - The last decimal is always wrapped in <span class="amount__tail" />
// - If the first decimal (second-to-last digit overall) is non-zero,
//   it is included inside the same span as well.
function AmountWithTailedDecimals({
    value,
    tailClassName = "amount__tail",
}: {
    value: number;
    tailClassName?: string;
}) {
    const fixed = value.toFixed(2); // e.g., "12.05"
    const negative = fixed.startsWith("-");
    const unsigned = negative ? fixed.slice(1) : fixed;
    const [intPart, decPart = "00"] = unsigned.split(".");
    const d1 = decPart[0]; // second-to-last digit overall
    const d2 = decPart[1]; // last digit overall
    const wrapTwo = d1 !== "0";

    return (
        <span className="amount">
            {negative && "-"}
            {intPart}.
            {wrapTwo ? (
                // e.g. 12.<span>20</span>
                <span className={tailClassName}>
                    {d1}
                    {d2}
                </span>
            ) : (
                // e.g. 12.0<span>5</span>
                <>
                    0<span className={tailClassName}>{d2}</span>
                </>
            )}
        </span>
    );
}

// TaskListItem
const TaskListItem = ({
    task,
    onTaskClick,
}: {
    task: z.infer<typeof TaskListItemSchema>;
    onTaskClick: (taskId: string) => void;
}) => {
    // ⬇️ Refs + sizing state for the stretchable background
    const rightRef = useRef<HTMLDivElement | null>(null);
    const rightContainerRef = useRef<HTMLDivElement | null>(null);

    // SVG "design" constants (in viewBox units of the original asset)
    const HEIGHT_UNITS = 77; // original viewBox height
    const STRAIGHT_START_X = 28; // where the top straight segment begins
    const DEFAULT_RIGHT_X = 106.5; // original right edge

    // Unique gradient id per item to avoid <defs> collisions
    const gradId = useId();
    const { t } = useTranslation();

    // ⬇️ Pinned arrows (measure container + one hidden arrow to compute count)
    const arrowsRowRef = useRef<HTMLDivElement | null>(null);
    const arrowMeasureRef = useRef<HTMLSpanElement | null>(null);
    const [arrowCount, setArrowCount] = useState(0);

    useLayoutEffect(() => {
        if (!task.isPinned) {
            setArrowCount(0);
            return;
        }
        const row = arrowsRowRef.current;
        const meas = arrowMeasureRef.current;
        if (!row || !meas) return;

        const GAP_PX = 1; // exactly as requested
        const EPS = 0.5; // tiny safety margin against sub-pixel rounding

        const compute = () => {
            const containerW = row.getBoundingClientRect().width;
            const iconW = meas.getBoundingClientRect().width;
            if (!containerW || !iconW) {
                setArrowCount(0);
                return;
            }
            // lock the icon width for the real icons (prevents layout drift)
            row.style.setProperty("--arrow-w", `${iconW}px`);
            // floor((W + gap - EPS) / (icon + gap)) avoids fractional spillover
            const n = Math.max(
                0,
                Math.floor((containerW + GAP_PX - EPS) / (iconW + GAP_PX)),
            );
            setArrowCount(n);
        };

        // initial compute + react to resizes
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(row);
        ro.observe(meas);
        return () => ro.disconnect();
    }, [task.isPinned]);

    // Background UI dimensions
    const [bg, setBg] = useState<{
        cssWidthPx: number; // width we apply to the wrapper (px)
        vbWidth: number; // viewBox width (units)
        rightX: number; // dynamic right X used in the path & gradient (units)
        ready: boolean;
    }>({
        cssWidthPx: 0,
        vbWidth: DEFAULT_RIGHT_X,
        rightX: DEFAULT_RIGHT_X,
        ready: false,
    });

    // Keep the right-side background width in sync with content width
    useLayoutEffect(() => {
        const rightEl = rightRef.current;
        const contentEl = rightContainerRef.current;
        if (!rightEl || !contentEl) return;

        const update = () => {
            // Pixel height of the SVG area -> scale factor from units to pixels
            const hPx = Math.max(1, rightEl.getBoundingClientRect().height);
            const unitToPx = hPx / HEIGHT_UNITS; // same for X and Y (we'll enforce that)

            // Content width in pixels
            const contentPx = contentEl.getBoundingClientRect().width;

            // Constant pixels we want to keep on the right edge (visual pad)
            const RIGHT_PAD_PX = 12;

            // Left "cap" width in pixels should remain constant as the card height changes
            // It's exactly the distance from x=0 to the straight start (STRAIGHT_START_X) in units -> convert to px:
            const leftCapPx = STRAIGHT_START_X * unitToPx;

            // Final background width in pixels: left cap + content + right pad
            const cssWidthPx = Math.round(leftCapPx + contentPx + RIGHT_PAD_PX);

            // Keep X/Y scale identical by choosing a viewBox width that matches that pixel width at the same scale
            const vbWidth = cssWidthPx / unitToPx;

            // The right edge of the path is simply the end of the viewBox
            const rightX = vbWidth;

            setBg({ cssWidthPx, vbWidth, rightX, ready: true });
        };

        update();

        // React to both height and content width changes
        const ro = new ResizeObserver(update);
        ro.observe(rightEl);
        ro.observe(contentEl);

        return () => ro.disconnect();
    }, []);

    return (
        <div
            className="task-list__item"
            data-task-status={task.taskStatus}
            data-user-relation={task.userRelation}
            // }

            data-no-spots-left={
                task.type === "WHEEL_SPIN" ||
                task.type === "REFERRAL" ||
                task.type === "BRIDGE_CHECK_IN" ||
                task.type === "SCRATCH" ||
                task.type === "PARTNER_LINK" ||
                task.type === "AB_TEST" ||
                task.type === "GAE" ||
                task.type === "GENERIC_LINK"
                    ? !task.isActionable
                    : !task.isActionable ||
                      (task.spotsLeft === 0 &&
                          !(
                              task.type === "CHECK_IN_CAMPAIGN" &&
                              task.userRelation === "in-progress"
                          ))
            }
            data-is-pinned={task.isPinned}
            data-pin-level={task.pinLevel}
            data-pinned-position={task.pinnedPosition}
            data-task-type={task.type}
            onClick={() => {
                sendHapticFeedback({ type: "light" });
                onTaskClick(task.id);
            }}
        >
            {/* New chip for AB_TEST items */}
            {(task.type === "AB_TEST" || task.type === "REFERRAL") && (
                <div className="task-list__item__new-chip">
                    {(() => {
                        const NewChipIcon = getNewChipForLocale(i18n.language);
                        return <NewChipIcon />;
                    })()}
                </div>
            )}
            {task.type === "GAE" && (
                <div className="task-list__item__beta-chip">
                    {(() => {
                        const BetaChipIcon = getBetaChipForLocale(
                            i18n.language,
                        );
                        return <BetaChipIcon />;
                    })()}
                </div>
            )}

            <div className="task-list__item__left">
                <div className="task-list__item__left__img-container">
                    <img src={task.imgUrl} alt={task.title} />

                    <div className="task-list__item__left__status-indicator">
                        <div className="task-list__item__left__status-indicator__inner" />
                    </div>

                    {(task.type === "CHECK_IN_CAMPAIGN" ||
                        task.type === "BRIDGE_CHECK_IN") && (
                        <div className="task-list__item__left__badge check-in">
                            <CheckInBadge />
                        </div>
                    )}
                    {task.type === "SURVEY" && (
                        <div className="task-list__item__left__badge survey">
                            <SurveyBadge />
                        </div>
                    )}
                </div>
                <div
                    className={`task-list__item__left__main ${
                        task.isPinned ? "pinned" : ""
                    }`}
                >
                    <h3 className="task-list__item__left__main__title">
                        {task.title}
                    </h3>

                    <p className="task-list__item__left__main__subtitle">
                        {task.type === "WHEEL_SPIN" ||
                        task.type === "SCRATCH" ||
                        task.type === "BRIDGE_CHECK_IN" ||
                        task.type === "REFERRAL" ||
                        task.type === "PARTNER_LINK" ||
                        task.type === "AB_TEST" ||
                        task.type === "GAE" ||
                        task.type === "GENERIC_LINK"
                            ? (() => {
                                  const now = Date.now();

                                  // Legacy SCRATCH kept as "coming soon"
                                  if (task.type === "SCRATCH") {
                                      return t("TaskList.comingSoon");
                                  }

                                  // BRIDGE_CHECK_IN countdown logic
                                  if (task.type === "BRIDGE_CHECK_IN") {
                                      if (
                                          task.brCanCheckNow === true ||
                                          task.brNextEligibleAt == null ||
                                          task.brNextEligibleAt <= now
                                      ) {
                                          return t("TaskList.checkInNow");
                                      }
                                      const remaining =
                                          task.brNextEligibleAt - now;
                                      return t("TaskList.checkInAgainIn", {
                                          countdown:
                                              formatShortCountdown(remaining),
                                      });
                                  }

                                  if (task.type === "PARTNER_LINK") {
                                      return t("TaskList.earnCoinsByPlaying");
                                  }

                                  if (task.type === "REFERRAL") {
                                      return t("TaskList.shareAndEarn");
                                  }

                                  if (task.type === "AB_TEST") {
                                      return t("TaskList.chooseAndEarn");
                                  }

                                  if (task.type === "GAE") {
                                      return t(
                                          "TaskList.turnSkillsIntoRewards",
                                      );
                                  }

                                  if (task.type === "GENERIC_LINK") {
                                      return task.subtitle ?? "";
                                  }

                                  // WHEEL_SPIN countdown logic
                                  if (
                                      task.stwCanSpinNow === true ||
                                      task.stwCanSpinAt == null ||
                                      task.stwCanSpinAt <= now
                                  ) {
                                      return t("TaskList.spinNow");
                                  }
                                  const remaining =
                                      (task.stwCanSpinAt ?? now) - now;
                                  return t("TaskList.spinAgainIn", {
                                      countdown:
                                          formatShortCountdown(remaining),
                                  });
                              })()
                            : // : `${formatDuration(task.duration)} • ${
                              //       task.spotsLeft
                              //   } left • ${timeAgo(
                              //       task.createdAt!,
                              //       i18n.language
                              //   )}`}

                              `${formatDuration(task.duration)} • ${t(
                                  "TaskList.spotsLeft",
                                  {
                                      spotsLeft: task.spotsLeft,
                                  },
                              )} • ${timeAgo(task.createdAt!, i18n.language)}`}
                    </p>

                    {/* Pinned arrow row (only rendered for pinned tasks) */}
                    {task.isPinned && (
                        <div
                            className="task-list__item__left__main__pinned-arrows"
                            ref={arrowsRowRef}
                            aria-hidden="true"
                        >
                            {/* Hidden measuring arrow: same CSS size, no layout impact */}
                            <span
                                className="pinned-arrows__icon pinned-arrows__icon--measure"
                                ref={arrowMeasureRef}
                            >
                                <InlineArrowRight />
                            </span>
                            {Array.from({ length: arrowCount }).map((_, i) => {
                                const isDim = i % 2 === 1;
                                return (
                                    <span
                                        className={`pinned-arrows__icon ${
                                            isDim ? "dim" : ""
                                        }`}
                                        key={i}
                                    >
                                        <InlineArrowRight />
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {task.primaryReward.type === "TOKEN" &&
            (task.type === "CHECK_IN_CAMPAIGN" ||
                task.type === "SURVEY" ||
                task.type === "PARTNER_CAMPAIGN_ACTION") ? (
                <div className="task-list__item__right" ref={rightRef}>
                    <div
                        className="task-list__item__right__bg-svg"
                        // Apply a width only after we've measured once to avoid initial flicker
                        style={
                            bg.ready
                                ? { width: `${bg.cssWidthPx}px` }
                                : undefined
                        }
                    >
                        <svg
                            // Set width/height via CSS. Here we keep aspect by matching viewBox to CSS box.
                            viewBox={`0 0 ${bg.vbWidth} ${HEIGHT_UNITS}`}
                            xmlns="http://www.w3.org/2000/svg"
                            preserveAspectRatio="none" // safe because our CSS box matches the viewBox ratio
                            aria-hidden="true"
                            focusable="false"
                        >
                            <path
                                // Only the right edge (and thus the H command) changes; all left control points are fixed.
                                d={`M21.5 38.5C21.5 25.9118 21.5 19.6176 23.715 14.7121C26.2202 9.16375 30.6638 4.72011 36.2122 2.21492C41.1177 0 47.4118 0 ${STRAIGHT_START_X} 0H${bg.rightX}V${HEIGHT_UNITS}H0C11.8741 ${HEIGHT_UNITS} 21.5 67.3741 21.5 55.5V38.5Z`}
                                fill={`url(#${gradId})`}
                            />
                            <defs>
                                <linearGradient
                                    id={gradId}
                                    x1={bg.rightX}
                                    y1="0"
                                    x2="-0.5"
                                    y2={HEIGHT_UNITS}
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop
                                        stopColor="white"
                                        stopOpacity="0.086"
                                    />
                                    <stop
                                        offset="1"
                                        stopColor="#5536FF"
                                        stopOpacity="0.3"
                                    />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>

                    <div
                        className="task-list__item__right__container"
                        ref={rightContainerRef}
                    >
                        <div className="task-list__item__right__container__amount">
                            <AmountWithTailedDecimals
                                value={task.primaryReward.amount}
                            />
                        </div>
                        <div
                            className={`task-list__item__right__container__unit ${task.primaryReward.unit.toLowerCase()}`}
                        >
                            {task.primaryReward.unit === "WLD" && <WldIcon />}
                            {task.primaryReward.unit === "GOLD" && <GoldIcon />}
                            {task.primaryReward.unit === "USDC" && <UsdcIcon />}

                            {task.primaryReward.unit === "QUARK" && (
                                <img
                                    src={QuarkLogoImg}
                                    alt=""
                                    className="task-list__item__right__container__unit-icon"
                                />
                            )}
                            {task.primaryReward.unit === "REDD" && (
                                <img
                                    src={ReddLogoImg}
                                    alt=""
                                    className="task-list__item__right__container__unit-icon"
                                />
                            )}
                            {task.primaryReward.unit === "SUSHI" && (
                                <img
                                    src={SushiLogoImg}
                                    alt=""
                                    className="task-list__item__right__container__unit-icon"
                                />
                            )}
                            {task.primaryReward.unit === "ORO" && (
                                <img
                                    src={OroLogoImg}
                                    alt=""
                                    className="task-list__item__right__container__unit-icon"
                                />
                            )}
                            {task.primaryReward.unit === "ORB" && (
                                <img
                                    src={OrbLogoImg}
                                    alt=""
                                    className="task-list__item__right__container__unit-icon"
                                />
                            )}

                            <p>{task.primaryReward.unit}</p>
                        </div>

                        <div className="task-list__item__right__container__light-blob">
                            <LightBlob />
                        </div>
                    </div>
                </div>
            ) : task.type === "WHEEL_SPIN" ||
              task.type === "SCRATCH" ||
              task.type === "BRIDGE_CHECK_IN" ||
              task.type === "PARTNER_LINK" ||
              task.type === "AB_TEST" ||
              task.type === "GAE" ||
              task.type === "GENERIC_LINK" ||
              task.type === "REFERRAL" ? (
                <div className="task-list__item__right-icon">
                    <FontAwesomeIcon icon={faChevronRight} />
                </div>
            ) : null}
        </div>
    );
};

export default function TaskList({ tasks, onTaskClick }: TaskListProps) {
    const { state: appState } = useSlice("appSlice");
    const [, setCurrentTime] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="task-list">
            {appState.loading?.appSession ? (
                <div className="task-list__skeletons">
                    {[...Array(5)].map((_, idx) => (
                        <Skeleton
                            key={idx}
                            baseColor="#ffffff"
                            highlightVariation={50}
                            borderRadius={"5vw"}
                            height={"78px"}
                            width={"100%"}
                            opacity={0.2}
                        />
                    ))}
                </div>
            ) : (
                <>
                    {tasks.map((task) => (
                        <TaskListItem
                            key={task.id}
                            task={task}
                            onTaskClick={onTaskClick}
                        />
                    ))}
                </>
            )}
        </div>
    );
}
